import {JSX, useCallback, useEffect, useMemo, useReducer, useState} from 'react';
import {arrayMove} from '@dnd-kit/sortable';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {Grid} from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { countBy, filter, flatMap, isEmpty, isNil, keys, maxBy, minBy, toLower, toString, trim, uniq } from 'lodash';
import { useSnackbar } from 'notistack';
import { CompanyFundSummary } from 'api';
import { RESET, SYSTEM_COLLAPSE, SYSTEM_EXPAND, USER_EXPAND } from 'common/actions/row-groups/types';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY } from 'common/constants/services/firms';
import { useFormat } from 'common/hooks';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { RowsGroups } from 'common/reducers/rowsGroups/types';
import { useStore } from 'common/store';
import { UseFormatValues } from 'common/types/format';
import { ScalarSpreadsheetProps } from 'common/types/scalarSpreadsheet';
import { UseStoreValues } from 'common/types/store';
import { COLUMN_TYPES, Filter, SortKey, SummaryGroupBy } from 'common/types/summaryDefinitions';
import { CustomCompactContainer, CustomContainer, CustomScalarSpreadsheet, Tab } from 'components';
import AIAssistantInput from 'components/AIAssistantInput';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import {GridSkeleton} from 'components/Grid';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import ChartDataContext from 'components/WithChart/ChartDataContext';
import {useLayoutContext} from 'context/LayoutContext';
import {useSummaryDefinitions} from 'context/SummaryDefinitionsContext';
import { useExportSignal } from 'features/pdfExport';
import {
  FIRM_SUMMARY_CONTAINER_CLASSNAME,
  FIRM_SUMMARY_KEY,
  FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS,
  FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
  FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
  FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
  FIRM_SUMMARY_TITLE,
  FIRM_SUMMARY_VALUES_SHOWN_PUBLISHED_TITLE,
} from 'pages/Firms/common/constants';
import {useFirmMeasurementDateFilter} from 'pages/Firms/hooks/useFirmMeasurementDateFilter';
import {FirmSummarySpreadsheet, useFirmSummarySheet} from 'pages/Firms/hooks/useFirmSummarySheet';
import {getOrderedColumnTypes} from 'pages/Firms/hooks/useFirmSummarySheet/config/createColumns';
import FilterChipsBar from 'pages/Firms/pages/FirmSummary/FilterChipsBar';
import SortChipsBar from 'pages/Firms/pages/FirmSummary/SortChipsBar';
import { InformationMessage } from 'pages/Funds/components';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { useGetFirmChartData, useGetFirmSummary, useGetFirmSummaryData } from 'services/hooks/firms';
import { SummaryRow } from 'services/hooks/firms/useGetFirmSummaryData';
import { useAIAssistSummary } from 'services/hooks/summaryDefinitions';
import { generateColumnKey, getArrayValue, getObjectValue, isValidDate } from 'utilities';
import { FirmCompanySummary, FundByCompany, UseFirmSummaryTabParams } from './useFirmSummaryTab.types';

const getRowKey = (row: FundByCompany): string =>
  generateColumnKey({
    id: getObjectValue(row).company_name,
    name: getObjectValue(row).fund_name,
    prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
  });

const parseToNumber = (value: unknown): number | null => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const toNullableString = (value: unknown): string | null =>
  value === null || value === undefined ? null : toString(value);

const getComparableValue = (row: FundByCompany, colType: string): string | number | null => {
  const safeRow = getObjectValue(row);

  switch (colType) {
    case COLUMN_TYPES.COMPANY_NAME:
      return safeRow.company_name ?? '';
    case COLUMN_TYPES.FUND_NAME:
      return safeRow.fund_name ?? '';
    case COLUMN_TYPES.VALUATION_DATE:
      return safeRow.measurement_date ? new Date(safeRow.measurement_date).getTime() : null;
    case COLUMN_TYPES.INITIAL_INVESTMENT_DATE:
      return safeRow.initial_investment_date ? new Date(safeRow.initial_investment_date).getTime() : null;
    case COLUMN_TYPES.MOST_RECENT_INVESTMENT_DATE:
      return safeRow.most_recent_investment_date ? new Date(safeRow.most_recent_investment_date).getTime() : null;
    case COLUMN_TYPES.DILUTED_OWNERSHIP:
      return parseToNumber(safeRow.diluted_ownership);
    case COLUMN_TYPES.OUTSTANDING_OWNERSHIP:
      return parseToNumber(safeRow.outstanding_ownership);
    case COLUMN_TYPES.INVESTED_CAPITAL:
      return parseToNumber(safeRow.fund_cost_basis);
    case COLUMN_TYPES.REALIZED_VALUE:
      return parseToNumber(safeRow.realized_value);
    case COLUMN_TYPES.UNREALIZED_VALUE:
      return parseToNumber(safeRow.unrealized_value);
    case COLUMN_TYPES.TOTAL_VALUE:
      return parseToNumber(safeRow.total_value);
    case COLUMN_TYPES.CURRENT_FUND_VALUE:
      return parseToNumber(safeRow.current_fund_value ?? safeRow.unrealized_value);
    case COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE:
      return parseToNumber(safeRow.breakeven_exit_equity_value_max ?? safeRow.breakeven_exit_equity_value_min);
    case COLUMN_TYPES.EQUITY_VALUE:
      return parseToNumber(safeRow.equity_value);
    case COLUMN_TYPES.ENTERPRISE_VALUE:
      return parseToNumber(safeRow.enterprise_value);
    case COLUMN_TYPES.TOTAL_INSIGHT_PREFERENCE:
      return parseToNumber(safeRow.total_insight_preference);
    case COLUMN_TYPES.FUND_PREFERENCE:
      return parseToNumber(safeRow.fund_preference);
    case COLUMN_TYPES.SPECIFIC_PREFERENCE:
      return parseToNumber(safeRow.specific_preference);
    case COLUMN_TYPES.TOTAL_COMPANY_PREFERENCE:
      return parseToNumber(safeRow.total_company_preference);
    case COLUMN_TYPES.GROSS_IRR:
      return parseToNumber(safeRow.gross_irr);
    case COLUMN_TYPES.MOIC: {
      const invested = parseToNumber(safeRow.fund_cost_basis);
      const total = parseToNumber(safeRow.total_value);
      if (!invested || invested === 0) {
        return 0;
      }
      return (total ?? 0) / invested;
    }
    case COLUMN_TYPES.PERCENT_CHANGE_FROM_PREVIOUS:
      return parseToNumber(safeRow.percent_change_from_previous);
    case COLUMN_TYPES.IMPLIED_ARR_MULTIPLE:
      return parseToNumber(safeRow.implied_arr_multiple);
    case COLUMN_TYPES.DCF_DISCOUNT_RATE:
      return parseToNumber(safeRow.dcf_discount_rate);
    case COLUMN_TYPES.PROJECTED_REVENUE_YEAR_1:
      return parseToNumber(safeRow.projected_revenue_year_1);
    case COLUMN_TYPES.PROJECTED_REVENUE_YEAR_2:
      return parseToNumber(safeRow.projected_revenue_year_2);
    case COLUMN_TYPES.PROJECTED_REVENUE_YEAR_3:
      return parseToNumber(safeRow.projected_revenue_year_3);
    case COLUMN_TYPES.ALLOCATION_METHOD:
      return safeRow.allocation_method ?? '';
    case COLUMN_TYPES.ENTERPRISE_VALUATION_METHOD:
      return safeRow.enterprise_valuation_method ?? '';
    case COLUMN_TYPES.VALUATION_APPROACH:
      return safeRow.valuation_approach ?? '';
    case COLUMN_TYPES.VALUATION_STATUS:
      return safeRow.valuation_status ?? '';
    default:
      return safeRow[colType] ?? null;
  }
};

const matchesFilter = (row: FundByCompany, filter: Filter, exclusionSet: Set<string>): boolean => {
  const [colType, [op, rawValue]] = filter;
  if (exclusionSet.has(colType)) {
    return true;
  }

  const rowValue = getComparableValue(row, colType);
  if (rowValue === null || typeof rawValue === 'undefined' || rawValue === null) {
    return false;
  }

  const normalizedRowValue = rowValue;
  const normalizedFilterValue =
    typeof normalizedRowValue === 'number' ? Number(rawValue) : toString(rawValue);

  if (typeof normalizedRowValue === 'number' && Number.isNaN(normalizedFilterValue)) {
    return false;
  }

  switch (op) {
    case '=':
      return normalizedRowValue === normalizedFilterValue;
    case '!=':
      return normalizedRowValue !== normalizedFilterValue;
    case '>':
      return Number(normalizedRowValue) > Number(normalizedFilterValue);
    case '>=':
      return Number(normalizedRowValue) >= Number(normalizedFilterValue);
    case '<':
      return Number(normalizedRowValue) < Number(normalizedFilterValue);
    case '<=':
      return Number(normalizedRowValue) <= Number(normalizedFilterValue);
    case 'contains':
      return toString(normalizedRowValue).toLowerCase().includes(toString(normalizedFilterValue).toLowerCase());
    default:
      return false;
  }
};

const applyFiltersWithHierarchy = (
  rows: FundByCompany[],
  filters: Filter[],
  filterExclusions: string[]
): FundByCompany[] => {
  const exclusionSet = new Set(filterExclusions ?? []);
  const activeFilters = (filters ?? []).filter(
    ([col, [, value]]) => !exclusionSet.has(col) && value !== null && typeof value !== 'undefined' && `${value}` !== ''
  );

  if (!activeFilters.length) {
    return rows;
  }

  const groups = new Map<string, { parent?: FundByCompany; children: FundByCompany[] }>();
  const groupOrder: string[] = [];
  const orphans: FundByCompany[] = [];

  rows.forEach(row => {
    const key = getRowKey(row);
    const parentKey = getObjectValue(row).parent_key;

    if (getObjectValue(row).is_parent) {
      if (!groups.has(key)) {
        groupOrder.push(key);
      }
      groups.set(key, { parent: row, children: getObjectValue(groups.get(key))?.children ?? [] });
    } else if (parentKey) {
      if (!groups.has(parentKey)) {
        groupOrder.push(parentKey);
      }
      const currentGroup = groups.get(parentKey) ?? { parent: undefined, children: [] };
      currentGroup.children.push(row);
      groups.set(parentKey, currentGroup);
    } else {
      orphans.push(row);
    }
  });

  const matchesAll = (row: FundByCompany) => activeFilters.every(filter => matchesFilter(row, filter, exclusionSet));
  const result: FundByCompany[] = [];

  groupOrder.forEach(key => {
    const group = groups.get(key);
    if (!group) {
      return;
    }
    const filteredChildren = group.children.filter(matchesAll);
    const includeParent = group.parent ? matchesAll(group.parent) || filteredChildren.length > 0 : false;

    if (includeParent && group.parent) {
      result.push(group.parent);
    } else if (!filteredChildren.length) {
      return;
    }

    result.push(...filteredChildren);
  });

  orphans.forEach(row => {
    if (matchesAll(row)) {
      result.push(row);
    }
  });

  return result;
};

const sortGroupedRows = (rows: FundByCompany[], sortKeys: SortKey[]): FundByCompany[] => {
  const activeSortKeys = (sortKeys ?? []).filter(([col]) => Boolean(col));

  if (!activeSortKeys.length) {
    return rows;
  }

  const comparator = (a: FundByCompany, b: FundByCompany) => {
    let result = 0;
    activeSortKeys.some(([col, direction]) => {
      const aVal = getComparableValue(a, col);
      const bVal = getComparableValue(b, col);

      if (aVal === bVal) {
        return false; // continue to next sort key
      }

      if (aVal === null || typeof aVal === 'undefined') {
        result = 1;
        return true; // break
      }
      if (bVal === null || typeof bVal === 'undefined') {
        result = -1;
        return true; // break
      }

      let diff = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        diff = aVal - bVal;
      } else {
        diff = toString(aVal).localeCompare(toString(bVal), undefined, { sensitivity: 'base' });
      }

      if (diff !== 0) {
        result = direction === 'asc' ? diff : -diff;
        return true; // break
      }
      return false;
    });
    return result;
  };

  const groups = new Map<string, { parent?: FundByCompany; children: FundByCompany[] }>();
  const groupOrder: string[] = [];
  const orphans: FundByCompany[] = [];

  rows.forEach(row => {
    const key = getRowKey(row);
    const parentKey = getObjectValue(row).parent_key;

    if (getObjectValue(row).is_parent) {
      if (!groups.has(key)) {
        groupOrder.push(key);
      }
      groups.set(key, { parent: row, children: getObjectValue(groups.get(key))?.children ?? [] });
    } else if (parentKey) {
      if (!groups.has(parentKey)) {
        groupOrder.push(parentKey);
      }
      const currentGroup = groups.get(parentKey) ?? { parent: undefined, children: [] };
      currentGroup.children.push(row);
      groups.set(parentKey, currentGroup);
    } else {
      orphans.push(row);
    }
  });

  const sortedGroups = groupOrder
    .map(key => ({ key, group: groups.get(key) }))
    .filter(item => item.group)
    .sort((a, b) => {
      const left = a?.group?.parent ?? a?.group?.children?.[0];
      const right = b?.group?.parent ?? b?.group?.children?.[0];

      if (!left || !right) {
        return 0;
      }

      return comparator(left, right);
    });

  const result: FundByCompany[] = [];

  sortedGroups.forEach(item => {
    const group = item?.group;
    if (!group) {
      return;
    }
    if (group.parent) {
      result.push(group.parent);
    }
    if (group.children.length) {
      result.push(...[...group.children].sort(comparator));
    }
  });

  if (orphans.length) {
    result.push(...[...orphans].sort(comparator));
  }

  return result;
};

const normalizeDefinitionRows = (rows: SummaryRow[], measurementDate?: string): FundByCompany[] => {
  // Build parent_key map from company_rollup rows
  const parentKeyByCompany = new Map<string, string>();
  rows.forEach(row => {
    if (row.row_type === 'company_rollup') {
      const key = generateColumnKey({
        id: row.company_name ?? '',
        name: row.fund_name ?? '',
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      parentKeyByCompany.set(row.company_name ?? '', key);
    }
  });

  return rows.map(row => {
    const isRollup = row.row_type === 'company_rollup';
    const isCompany = row.row_type === 'company';
    const parentKey = isCompany ? (parentKeyByCompany.get(row.company_name ?? '') ?? null) : null;

    return {
      ...FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
      company_id: row.id,
      company_name: row.company_name,
      company_slug: row.company_slug ?? '',
      company_captable_currency: FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
      fund_id: 0,
      fund_name: row.fund_name,
      fund_slug: row.fund_slug ?? '',
      fund_currency: FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
      exchange_rate_to_fund_currency: '1',
      measurement_date: measurementDate ?? '',
      initial_investment_date: row.initial_investment_date,
      most_recent_investment_date: row.most_recent_investment_date,
      diluted_ownership: toNullableString(row.diluted_ownership),
      outstanding_ownership: toNullableString(row.outstanding_ownership),
      fund_cost_basis: toNullableString(row.invested_capital),
      realized_value: toNullableString(row.realized_value),
      unrealized_value: toNullableString(row.unrealized_value),
      total_value: toNullableString(row.total_value),
      gross_irr: toNullableString(row.gross_irr),
      moic: toNullableString(row.moic),
      x_factor: null,
      is_published: true,
      is_final: true,
      breakeven_exit_equity_value_min: toNullableString(row.breakeven_exit_equity_value_min),
      breakeven_exit_equity_value_max: toNullableString(row.breakeven_exit_equity_value_max),
      allocation_method: row.allocation_method ?? '',
      equity_value: toNullableString(row.equity_value),
      enterprise_valuation_method: row.enterprise_valuation_method ?? '',
      enterprise_value: toNullableString(row.enterprise_value),
      current_fund_value: toNullableString(row.current_fund_value ?? row.unrealized_value),
      percent_change_from_previous: toNullableString(row.percent_change_from_previous),
      valuation_status: row.valuation_status ?? '',
      total_insight_preference: toNullableString(row.total_insight_preference),
      fund_preference: toNullableString(row.fund_preference),
      specific_preference: toNullableString(row.specific_preference),
      total_company_preference: toNullableString(row.total_company_preference),
      implied_arr_multiple: toNullableString(row.implied_arr_multiple),
      valuation_approach: row.valuation_approach ?? '',
      dcf_discount_rate: toNullableString(row.dcf_discount_rate),
      projected_revenue_year_1: toNullableString(row.projected_revenue_year_1),
      projected_revenue_year_2: toNullableString(row.projected_revenue_year_2),
      projected_revenue_year_3: toNullableString(row.projected_revenue_year_3),
      funds_count: isRollup ? (row.funds_count ?? 1) : 1,
      is_parent: isRollup,
      parent_key: parentKey,
      parent_type: 'company',
    } as FundByCompany;
  });
};

const useFirmSummaryTab = (params?: UseFirmSummaryTabParams) => {
  const { spreadsheetDefinition, definitionId, onSortToggle, valueTypeById, onClearFilters, onRemoveFilter, onClearSorts, onRemoveSort, onAddColumn } = params ?? {};
  const [storeValue] = useStore() as UseStoreValues;
  const { companyList: currentCompanies, firmInfo: currentFirm } = getObjectValue(storeValue);
  const { id: currentFirmId } = getObjectValue(currentFirm);

  const isEnhancedMode = !isNil(definitionId);

  const { activeColumnWidths, setColumnWidth, setColumnOrder, updateDefinition, activeDefinition, refetch } =
    useSummaryDefinitions();

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const aiAssist = useAIAssistSummary(currentFirmId);

  const handleAIAssistSubmit = useCallback(
    async (prompt: string) => {
      if (!spreadsheetDefinition || !activeDefinition) {
        return;
      }
      try {
        const result = await aiAssist.mutateAsync({
          prompt,
          current_definition: spreadsheetDefinition,
        });

        await updateDefinition(activeDefinition.id, {
          spreadsheet_definition: result.spreadsheet_definition,
        });

        await Promise.all([
          refetch(),
          queryClient.invalidateQueries({ queryKey: [FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY] }),
        ]);

        if (result.message) {
          enqueueSnackbar(result.message, { variant: 'success' });
        }
      } catch {
        enqueueSnackbar('AI assistant is temporarily unavailable. Please try again.', { variant: 'error' });
      }
    },
    [activeDefinition, aiAssist, enqueueSnackbar, queryClient, refetch, spreadsheetDefinition, updateDefinition]
  );

  const orderedColumnTypes = useMemo(
    () => getOrderedColumnTypes(spreadsheetDefinition),
    [spreadsheetDefinition]
  );

  const handleColumnReorder = useCallback(
    async (sourceColumn: string, targetColumn: string) => {
      const sourceIdx = orderedColumnTypes.indexOf(sourceColumn);
      const targetIdx = orderedColumnTypes.indexOf(targetColumn);
      if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) {
        return;
      }
      const newOrder = arrayMove(orderedColumnTypes, sourceIdx, targetIdx);

      // getOrderedColumnTypes filters out columns like company_name (handled separately by row headers).
      // Re-inject them at the front in their original sort order so the backend definition retains them.
      const orderedSet = new Set(orderedColumnTypes);
      const preservedColumns = Object.entries(spreadsheetDefinition?.columns ?? {})
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([, col]) => col)
        .filter(col => !orderedSet.has(col));

      const fullOrder = [...preservedColumns, ...newOrder];
      const columns = Object.fromEntries(fullOrder.map((col, idx) => [idx, col]));
      await setColumnOrder(columns);
    },
    [orderedColumnTypes, setColumnOrder, spreadsheetDefinition]
  );

  // Handle measurement date filter, query param, and context synchronization
  const {
    hasNoMeasurementDates,
    isLoadingMeasurementDate,
    isMostRecentMeasurementDate,
    mostRecentMeasurementDate,
    selectedMeasurementDate,
  } = useFirmMeasurementDateFilter();

  const resolvedMeasurementDate =
    !isNil(selectedMeasurementDate) && isValidDate(selectedMeasurementDate.date)
      ? selectedMeasurementDate.date
      : undefined;

  // For the enhanced endpoint a real YYYY-MM-DD date is always required.
  // When the "Most Recent" sentinel is active, fall back to the actual latest date.
  const resolvedMeasurementDateForEnhanced =
    resolvedMeasurementDate ??
    (isMostRecentMeasurementDate && isValidDate(mostRecentMeasurementDate?.date)
      ? mostRecentMeasurementDate.date
      : undefined);

  const hasBreakevenColumn = useMemo(
    () =>
      Object.values(spreadsheetDefinition?.columns ?? {}).includes(COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE),
    [spreadsheetDefinition]
  );
  const breakevenMultipleValue = hasBreakevenColumn ? Number(spreadsheetDefinition?.breakevenMultiple) : NaN;
  const breakevenMultiple = Number.isFinite(breakevenMultipleValue) && breakevenMultipleValue !== 1
    ? breakevenMultipleValue
    : undefined;

  const {
    data: firmSummary,
    isFetched: isFetchedFirmSummary,
    isFetching: isFetchingFirmSummary,
    isLoading: isLoadingFirmSummary,
  } = useGetFirmSummary({
    firmId: currentFirmId,
    measurementDate: resolvedMeasurementDate,
    breakevenMultiple,
    shouldQueryAutomatically: !isEnhancedMode && !isNil(currentFirmId) && !isLoadingMeasurementDate,
  });

  const {
    data: definitionData,
    isFetched: isFetchedDefinitionData,
    isFetching: isFetchingDefinitionData,
    isLoading: isLoadingDefinitionData,
  } = useGetFirmSummaryData({
    firmId: currentFirmId,
    definitionId,
    measurementDate: resolvedMeasurementDateForEnhanced,
    shouldQueryAutomatically:
      isEnhancedMode &&
      !isNil(currentFirmId) &&
      !isNil(definitionId) &&
      !isLoadingMeasurementDate &&
      !isNil(resolvedMeasurementDateForEnhanced),
  });

  // Prefetch chart data after spreadsheet data loads (non-blocking)
  const {
    data: chartData,
    isLoading: isLoadingChartData,
  } = useGetFirmChartData({
    firmId: currentFirmId,
    definitionId,
    shouldQueryAutomatically:
      isEnhancedMode &&
      !isNil(currentFirmId) &&
      !isNil(definitionId) &&
      isFetchedDefinitionData,
  });

  const chartDataContextValue = useMemo(
    () => ({ chartData, isLoading: isLoadingChartData, selectedDate: resolvedMeasurementDateForEnhanced }),
    [chartData, isLoadingChartData, resolvedMeasurementDateForEnhanced]
  );

  const isFetchedFirmSummaryActive = isEnhancedMode ? isFetchedDefinitionData : isFetchedFirmSummary;
  const isFetchingFirmSummaryActive = isEnhancedMode ? isFetchingDefinitionData : isFetchingFirmSummary;
  const isLoadingFirmSummaryActive = isEnhancedMode ? isLoadingDefinitionData : isLoadingFirmSummary;

  // Use backend-calculated Gross IRR (includes all cash flows from FundOwnershipDetails)
  const { all_funds: fundNames, companies, firm_gross_irr: firmGrossIRR, funds_gross_irr: fundsGrossIRR, companies_gross_irr: companiesGrossIRR } = getObjectValue(firmSummary);

  const [selectedFund, setSelectedFund] = useState<string>(FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS);

  const fundsAvailable = useMemo(() => {
    if (isEnhancedMode) {
      const uniqueFundNames = uniq(
        (definitionData?.rows ?? [])
          .filter(row => row.row_type === 'company')
          .map(row => row.fund_name)
          .filter(Boolean)
      );
      return [FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS, ...uniqueFundNames];
    }
    return [FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS, ...getArrayValue(fundNames)];
  }, [isEnhancedMode, definitionData, fundNames]);

  const isAllFundsSelected = useMemo<boolean>(
    () => selectedFund === FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS,
    [selectedFund]
  );

  // The most common fund currency across all funds
  const fundsCurrency = useMemo<string>(
    () =>
      maxBy(
        keys(countBy(flatMap(companies, 'funds'), 'fund_currency')),
        company => countBy(flatMap(companies, 'funds'), 'fund_currency')[company]
      ) ?? FIRM_SUMMARY_SPREADSHEET_DEFAULT_CURRENCY,
    [companies]
  );

  const groupBy: SummaryGroupBy = useMemo(
    () => spreadsheetDefinition?.groupBy ?? 'company',
    [spreadsheetDefinition]
  );

  const sortKeys = useMemo<SortKey[]>(() => spreadsheetDefinition?.sortKeys ?? [], [spreadsheetDefinition]);
  const filters = useMemo<Filter[]>(() => spreadsheetDefinition?.filters ?? [], [spreadsheetDefinition]);
  const filterExclusions = useMemo<string[]>(
    () => spreadsheetDefinition?.filterExclusions ?? [],
    [spreadsheetDefinition]
  );

  const fundsByCompany = useMemo<FundByCompany[]>(
    () => {
      if (isEnhancedMode) {
        return normalizeDefinitionRows(definitionData?.rows ?? [], resolvedMeasurementDateForEnhanced);
      }

      if (!companies) {
        return [];
      }

      return flatMap(companies, company => {
        const { company_id: companyId, company_name: companyName, funds } = company;

        const currentFund = funds[0];
        const currentCompany: FirmCompanySummary = { company_id: companyId, company_name: companyName };

        // Standalone
        if (funds.length === 1) {
          return [
            {
              ...currentCompany,
              ...currentFund,
              ...FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
            } as FundByCompany,
          ];
        }

        // All fund names for this company, separated by commas
        const fundName: CompanyFundSummary['fund_name'] = funds.map(fund => fund.fund_name).join(', ');
        // The latest most recent measurement date across all funds for this company
        const mostRecentValuationDate: CompanyFundSummary['measurement_date'] = toString(
          maxBy(funds, 'measurement_date')?.measurement_date
        );
        // The earliest initial investment date across all funds for this company
        const initialInvestmentDate: CompanyFundSummary['initial_investment_date'] = toString(
          minBy(funds, 'initial_investment_date')?.initial_investment_date
        );
        // The latest most recent investment date across all funds for this company
        const mostRecentInvestmentDate: CompanyFundSummary['most_recent_investment_date'] = toString(
          maxBy(funds, 'most_recent_investment_date')?.most_recent_investment_date
        );

        // The most common fund currency across all funds for this company
        const companyFundsCurrency = maxBy(
          keys(countBy(funds, 'fund_currency')),
          fund => countBy(funds, 'fund_currency')[fund]
        );

        const investedTotal = funds.reduce((acc, row) => acc + (parseToNumber(row.fund_cost_basis) ?? 0), 0);
        const realizedTotal = funds.reduce((acc, row) => acc + (parseToNumber(row.realized_value) ?? 0), 0);
        const unrealizedTotal = funds.reduce((acc, row) => acc + (parseToNumber(row.unrealized_value) ?? 0), 0);
        const totalValue = realizedTotal + unrealizedTotal;
        const currentFundValueTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.current_fund_value ?? row.unrealized_value) ?? 0),
          0
        );
        const totalInsightPreferenceTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.total_insight_preference) ?? 0),
          0
        );
        const fundPreferenceTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.fund_preference) ?? 0),
          0
        );
        const specificPreferenceTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.specific_preference) ?? 0),
          0
        );
        const totalCompanyPreferenceTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.total_company_preference) ?? 0),
          0
        );
        const breakevenExitEquityValueMinTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.breakeven_exit_equity_value_min) ?? 0),
          0
        );
        const breakevenExitEquityValueMaxTotal = funds.reduce(
          (acc, row) => acc + (parseToNumber(row.breakeven_exit_equity_value_max) ?? 0),
          0
        );
        const equityValueTotal = funds.reduce((acc, row) => acc + (parseToNumber(row.equity_value) ?? 0), 0);
        const enterpriseValueTotal = funds.reduce((acc, row) => acc + (parseToNumber(row.enterprise_value) ?? 0), 0);

        const companyFund: FundByCompany = {
          ...currentCompany,
          ...currentFund,
          diluted_ownership: null,
          outstanding_ownership: null,
          fund_cost_basis: toString(investedTotal),
          fund_name: fundName,
          funds_count: funds.length,
          initial_investment_date: initialInvestmentDate,
          is_parent: true,
          most_recent_investment_date: mostRecentInvestmentDate,
          parent_gross_irr: companiesGrossIRR ? companiesGrossIRR[companyId] : null,
          parent_key: null,
          parent_type: 'company',
          realized_value: toString(realizedTotal),
          total_value: toString(totalValue),
          unrealized_value: toString(unrealizedTotal),
          current_fund_value: toString(currentFundValueTotal),
          equity_value: toString(equityValueTotal),
          enterprise_value: toString(enterpriseValueTotal),
          total_insight_preference: toString(totalInsightPreferenceTotal),
          fund_preference: toString(fundPreferenceTotal),
          specific_preference: toString(specificPreferenceTotal),
          total_company_preference: toString(totalCompanyPreferenceTotal),
          breakeven_exit_equity_value_min: toString(breakevenExitEquityValueMinTotal),
          breakeven_exit_equity_value_max: toString(breakevenExitEquityValueMaxTotal),
          allocation_method: '',
          enterprise_valuation_method: '',
          valuation_approach: '',
          dcf_discount_rate: null,
          valuation_status: '',
          percent_change_from_previous: null,
          implied_arr_multiple: null,
          projected_revenue_year_1: null,
          projected_revenue_year_2: null,
          projected_revenue_year_3: null,
          fund_currency: companyFundsCurrency ?? fundsCurrency,
          measurement_date: mostRecentValuationDate,
          fund_id: 0,
          fund_slug: '',
          gross_irr: companiesGrossIRR ? companiesGrossIRR[companyId] : null,
        };

        const companyFundKey = generateColumnKey({
          id: companyFund.company_name,
          name: companyFund.fund_name,
          prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
        });

        const companyFunds = funds.map(
          fund =>
            ({
              ...currentCompany,
              ...fund,
              ...FIRM_SUMMARY_SPREADSHEET_NON_PARENT_ROW_GROUP,
              parent_key: companyFundKey,
            }) as FundByCompany
        );

        return [companyFund, ...companyFunds] as FundByCompany[];
      });
    },
    [companies, companiesGrossIRR, definitionData, fundsCurrency, isEnhancedMode, resolvedMeasurementDateForEnhanced]
  );

  const filteredFundsByCompany = useMemo<FundByCompany[]>(() => {
    if (isAllFundsSelected) {
      return fundsByCompany;
    }

    return filter(fundsByCompany, fund => toLower(trim(fund.fund_name)) === toLower(trim(selectedFund)));
  }, [fundsByCompany, isAllFundsSelected, selectedFund]);

  const filteredByDefinition = useMemo<FundByCompany[]>(
    () => isEnhancedMode ? filteredFundsByCompany : applyFiltersWithHierarchy(filteredFundsByCompany, filters, filterExclusions),
    [filteredFundsByCompany, filters, filterExclusions, isEnhancedMode]
  );

  const orderedFundsByCompany = useMemo<FundByCompany[]>(
    () => {
      // Enhanced rows arrive pre-sorted from the backend when "All Funds" is selected.
      // But once the user picks a specific fund, the frontend filters that sorted payload
      // down to a flat subset of child rows, so we need to re-apply sorting client-side.
      if (isEnhancedMode && isAllFundsSelected) {
        return filteredByDefinition;
      }

      return sortGroupedRows(filteredByDefinition, sortKeys);
    },
    [filteredByDefinition, sortKeys, isEnhancedMode, isAllFundsSelected]
  );

  const hasDraftFunds = useMemo<boolean>(
    () => orderedFundsByCompany.some(fund => !fund.is_published && !fund.is_final),
    [orderedFundsByCompany]
  );

  const hasNoCompanies = useMemo<boolean>(
    () => !isNil(currentCompanies) && isEmpty(currentCompanies),
    [currentCompanies]
  );

  const hasNoInvestments = useMemo<boolean>(
    () =>
      hasNoMeasurementDates ||
      (Boolean(isFetchedFirmSummaryActive) &&
        (isEnhancedMode ? (isNil(definitionData) || isEmpty(definitionData?.rows)) : isNil(firmSummary)) &&
        isEmpty(orderedFundsByCompany)),
    [definitionData, firmSummary, hasNoMeasurementDates, isFetchedFirmSummaryActive, isEnhancedMode, orderedFundsByCompany]
  );

  const isLoading = useMemo<boolean>(
    () => isLoadingFirmSummaryActive || isLoadingMeasurementDate,
    [isLoadingFirmSummaryActive, isLoadingMeasurementDate]
  );

  let firmSummaryExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (isLoading) {
    firmSummaryExportStatus = 'loading';
  } else if (hasNoCompanies || hasNoInvestments) {
    firmSummaryExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.FIRM_SUMMARY.id,
    status: firmSummaryExportStatus,
  });

  // Spreadsheet

  const rowGroupParentKeys = useMemo<string[]>(
    () =>
      uniq(
        orderedFundsByCompany
          .map(item => getObjectValue(item).parent_key)
          .filter(Boolean)
          .map(key => toString(key))
      ),
    [orderedFundsByCompany]
  );

  const rowGroupsInitialState = useMemo<RowsGroups>(
    () =>
      Object.fromEntries(rowGroupParentKeys.map(key => [key, isAllFundsSelected ? SYSTEM_COLLAPSE : SYSTEM_EXPAND])),
    [isAllFundsSelected, rowGroupParentKeys]
  );

  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, rowGroupsInitialState);

  useEffect(() => {
    if (rowGroupParentKeys.length > 0) {
      setRowGroups({ type: RESET, payload: Object.fromEntries(rowGroupParentKeys.map(key => [key, isAllFundsSelected ? SYSTEM_COLLAPSE : SYSTEM_EXPAND])) });
    }
  }, [rowGroupParentKeys, isAllFundsSelected]);

  const { spreadsheet: firmSummarySpreadsheet } = useFirmSummarySheet({
    fundsAvailable,
    fundsByCompany: orderedFundsByCompany,
    groupBy,
    isAllFundsSelected,
    selectedFund,
    setSelectedFund,
    firmGrossIRR,
    fundsGrossIRR,
    companiesGrossIRR,
    spreadsheetDefinition,
    onSortToggle,
    isEnhancedMode,
  });

  const spreadsheets = useMemo<FirmSummarySpreadsheet[]>(
    () =>
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: private 'yLength' property
      firmSummarySpreadsheet?.yLength ? [firmSummarySpreadsheet] : [],
    [firmSummarySpreadsheet]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: private 'shouldValidate' property
  const { onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets);

  const [format, formatDispatch] = useFormat({
    measurementDate: isMostRecentMeasurementDate ? mostRecentMeasurementDate : selectedMeasurementDate,
    page: CAP_TABLE_CURRENCY_PAGE,
    sourceCurrency: fundsCurrency,
    units: REGULAR_UNIT,
  }) as UseFormatValues;

  const firmSummarySpreadsheetProps = useMemo<ScalarSpreadsheetProps | null>(() => {
    if (firmSummarySpreadsheet) {
      return {
        ...firmSummarySpreadsheet,
        data: getArrayValue(firmSummarySpreadsheet?.data),
        format,
        formatDispatch,
        onChange,
        sheet: firmSummarySpreadsheet,
        workbook,
        rowGroups,
        setRowGroups,
        ...(isEnhancedMode && {
          columnWidths: activeColumnWidths,
          onColumnResize: setColumnWidth,
          columnTypes: orderedColumnTypes,
          onColumnReorder: handleColumnReorder,
          onAddColumn,
        }),
      } as unknown as ScalarSpreadsheetProps;
    }

    return null;
  }, [firmSummarySpreadsheet, format, formatDispatch, onChange, rowGroups, workbook, isEnhancedMode, activeColumnWidths, setColumnWidth, orderedColumnTypes, handleColumnReorder, onAddColumn]);

  const { isExportMode } = useLayoutContext();

  const hasExpandedRowGroup = useMemo(
    () => Object.values(rowGroups).some(rowGroup => [SYSTEM_EXPAND, USER_EXPAND].includes(rowGroup)),
    [rowGroups]
  );

  const firmSummaryTabContent = useMemo<JSX.Element | null>(() => {
    if (hasNoCompanies) {
      return null;
    }

    // Filters reduced the result set to zero rows — let the user know via chips bar
    const isFilteredEmpty = isEnhancedMode && filters.length > 0 && hasNoInvestments;

    if (hasNoInvestments && !isFilteredEmpty) {
      return null;
    }

    const aiInput = isEnhancedMode && !isExportMode ? (
      <AIAssistantInput
        onSubmit={handleAIAssistSubmit}
        isLoading={aiAssist.isPending}
        disabled={!activeDefinition}
      />
    ) : null;

    const sortChipsBar = isEnhancedMode && sortKeys.length > 0 && !isExportMode ? (
      <SortChipsBar
        sortKeys={sortKeys}
        valueTypeById={valueTypeById ?? new Map()}
        onClearAll={onClearSorts ?? (() => Promise.resolve())}
        onRemoveSort={onRemoveSort ?? (() => Promise.resolve())}
      />
    ) : null;

    const filterChipsBar = isEnhancedMode && filters.length > 0 && !isExportMode ? (
      <FilterChipsBar
        filters={filters}
        valueTypeById={valueTypeById ?? new Map()}
        onClearAll={onClearFilters ?? (() => Promise.resolve())}
        onRemoveFilter={onRemoveFilter ?? (() => Promise.resolve())}
      />
    ) : null;

    let gridContent: JSX.Element | null | false = null;
    if (isLoading) {
      gridContent = <GridSkeleton maxColumns={12} maxRows={4} />;
    } else if (isFilteredEmpty) {
      gridContent = (
        <Box>
          {aiInput}
          {sortChipsBar}
          {filterChipsBar}
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No results match the active filters.
            </Typography>
          </Box>
        </Box>
      );
    } else if (firmSummarySpreadsheetProps && firmSummarySpreadsheet) {
      gridContent = (
        <ChartDataContext.Provider value={chartDataContextValue}>
          <WorkbookContext.Provider value={workbookContextValue}>
            <CustomCompactContainer>
              <Grid
                className={clsx(FIRM_SUMMARY_CONTAINER_CLASSNAME, { expanded: hasExpandedRowGroup })}
                size={{ xs: 12 }}>
                {aiInput}
                {sortChipsBar}
                {filterChipsBar}
                <CustomScalarSpreadsheet {...firmSummarySpreadsheetProps} isLoading={isFetchingFirmSummaryActive} />
              </Grid>

              {!isExportMode && (
                <InformationMessage
                  displayMessage={hasDraftFunds}
                  message={FIRM_SUMMARY_VALUES_SHOWN_PUBLISHED_TITLE}
                />
              )}
            </CustomCompactContainer>
          </WorkbookContext.Provider>
        </ChartDataContext.Provider>
      );
    }

    return (
      <CustomContainer data-ref={PdfExportTargets.FIRM_SUMMARY.id}>
        <Grid className={FIRM_SUMMARY_CONTAINER_CLASSNAME} size={{ xs: 12 }}>
          {gridContent}
        </Grid>
      </CustomContainer>
    );
  }, [
    activeDefinition,
    aiAssist.isPending,
    chartDataContextValue,
    filters,
    firmSummarySpreadsheet,
    firmSummarySpreadsheetProps,
    handleAIAssistSubmit,
    hasDraftFunds,
    hasExpandedRowGroup,
    hasNoCompanies,
    hasNoInvestments,
    isFetchingFirmSummaryActive,
    isEnhancedMode,
    isExportMode,
    isLoading,
    onClearFilters,
    onClearSorts,
    onRemoveFilter,
    onRemoveSort,
    sortKeys,
    valueTypeById,
    workbookContextValue,
  ]);

  // Tab
  const firmSummaryTab = useMemo<Tab>(
    () =>
      ({
        id: FIRM_SUMMARY_KEY,
        label: FIRM_SUMMARY_TITLE,
        content: firmSummaryTabContent,
      }) as Tab,
    [firmSummaryTabContent]
  );

  // Reset selected fund when measurement date changes
  useEffect(() => {
    if (!isLoadingMeasurementDate) {
      setSelectedFund(FIRM_SUMMARY_SPREADSHEET_ALL_FUNDS);
    }
  }, [isLoadingMeasurementDate, selectedMeasurementDate]);

  // Synchronize rowGroups state with dynamic parent keys
  useEffect(() => {
    if (!isEmpty(rowGroupsInitialState)) {
      setRowGroups({ payload: rowGroupsInitialState, type: RESET });
    }
  }, [rowGroupsInitialState]);

  const isFilteredEmpty = isEnhancedMode && filters.length > 0 && hasNoInvestments;

  return { firmSummaryTab, hasNoCompanies, hasNoInvestments, isFilteredEmpty };
};

export default useFirmSummaryTab;
